import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { format, addDays, isSameDay, parseISO } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faXmark,
  faChevronLeft,
  faChevronRight,
  faCalendarXmark
} from "@fortawesome/free-solid-svg-icons";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import moment from "moment-timezone";

// Utility function to convert 24-hour time to 12-hour format with AM/PM
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function CustomCalendarNav({ month, onPreviousClick, onNextClick }) {
  const monthYear = format(month, "MMMM yyyy");
  return (
    <div className="text-gray-900 absolute top-0 left-0 right-0 flex justify-center items-center">
      <button onClick={onPreviousClick} className="mx-4 text-gray-700 hover:text-gray-900">
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>
      <span className="font-bold">{monthYear}</span>
      <button onClick={onNextClick} className="mx-4 text-gray-700 hover:text-gray-900">
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

export default function DateSelection({
  availability,
  bookings,
  blockedTimes = [], // Provider's blocked time slots
  serviceDuration,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  providerTimezone, // Provider's timezone from database (required for converting UTC bookings)
  minimumAdvanceNoticeHours = 0,
}) {
  // Use provider's timezone from database, fallback only if truly missing
  const timezone = providerTimezone || "America/New_York";
  // Generate one year of dates from today
  const allDays = useMemo(() => {
    const list = [];
    const today = new Date();
    for (let i = 0; i < 366; i++) {
      list.push(addDays(today, i));
    }
    return list;
  }, []);
  const sliderRef = useRef(null);

  // Compute available times for a day
  const extractDateTimeParts = useCallback((value) => {
    if (!value) {
      return { dateStr: "", timeStr: "" };
    }

    const sanitizeTime = (raw = "") => {
      const clean = raw.replace("Z", "").trim();
      return clean.slice(0, 5);
    };

    if (typeof value === "string") {
      const normalized = value.includes("T")
        ? value.split("T")
        : value.split(" ");
      const dateStr = normalized[0] || "";
      const timeStr = sanitizeTime(normalized[1] || "");
      return { dateStr, timeStr };
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const dateStr = `${value.getFullYear()}-${String(
        value.getMonth() + 1
      ).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
      const timeStr = `${String(value.getHours()).padStart(
        2,
        "0"
      )}:${String(value.getMinutes()).padStart(2, "0")}`;
      return { dateStr, timeStr };
    }

    return { dateStr: "", timeStr: "" };
  }, []);

  const getTimesForDay = useCallback((day) => {
    const dayAvail = availability.filter((slot) => {
      // Filter out closed slots
      if (slot.isClosed) {
        return false;
      }

      if (slot.type === "specific_date") {
        return isSameDay(day, parseISO(slot.specificDate));
      } else if (slot.type === "recurring") {
        return day.getDay() === slot.dayOfWeek;
      }
      return false;
    });

    // Ensure serviceDuration is a number
    const duration = Number(serviceDuration) || 60;

    let times = [];
    dayAvail.forEach((slot) => {
      // Parse start time (format: "HH:mm" or "HH:mm:ss")
      const startTimeParts = slot.startTime.split(":").map(Number);
      const sh = startTimeParts[0] || 0;
      const sm = startTimeParts[1] || 0;
      const startMinutes = sh * 60 + sm;

      // Parse end time (format: "HH:mm" or "HH:mm:ss")
      let endMinutes;
      if (slot.endTime) {
        const endTimeParts = slot.endTime.split(":").map(Number);
        const eh = endTimeParts[0] || 0;
        const em = endTimeParts[1] || 0;
        endMinutes = eh * 60 + em;
      } else {
        // If no end time, default to start + service duration
        endMinutes = startMinutes + duration;
      }

      // Debug: Log slot processing details
      if (process.env.NODE_ENV === 'development') {
        console.log(`[DateSelection] Processing slot: ${slot.startTime} - ${slot.endTime}, startMinutes: ${startMinutes}, endMinutes: ${endMinutes}, serviceDuration: ${duration}`);
      }

      // Generate time slots in serviceDuration increments
      // Continue until the next slot would exceed the end time
      let slotCount = 0;
      for (
        let cursor = startMinutes;
        cursor + duration <= endMinutes;
        cursor += duration
      ) {
        const hours = Math.floor(cursor / 60);
        const minutes = cursor % 60;
        const hh = String(hours).padStart(2, "0");
        const mm = String(minutes).padStart(2, "0");
        const timeStr = `${hh}:${mm}`;
        times.push(timeStr);
        slotCount++;

        // Debug: Log each generated slot
        if (process.env.NODE_ENV === 'development') {
          console.log(`[DateSelection] Generated slot ${slotCount}: ${timeStr} (cursor: ${cursor}, next: ${cursor + duration}, end: ${endMinutes})`);
        }
      }

      // Debug: Warn if no slots were generated
      if (process.env.NODE_ENV === 'development' && slotCount === 0) {
        console.warn(`[DateSelection] No slots generated for ${slot.startTime} - ${slot.endTime}. Check: startMinutes=${startMinutes}, endMinutes=${endMinutes}, serviceDuration=${duration}, condition: ${startMinutes} + ${duration} <= ${endMinutes} = ${startMinutes + duration <= endMinutes}`);
      }
    });
    // Remove duplicates and sort
    times = [...new Set(times)];

    // Debug: Log availability slots and generated times
    if (process.env.NODE_ENV === 'development' && dayAvail.length > 0) {
      console.log(`[DateSelection] Day: ${format(day, "yyyy-MM-dd")}, Service duration: ${duration} minutes`);
      console.log(`[DateSelection] Available slots:`, dayAvail);
      console.log(`[DateSelection] Generated times before filtering:`, times);
    }

    const selectedDateStr = format(day, "yyyy-MM-dd");

    // Calculate all blocked time ranges based on existing bookings
    // Only block time slots for bookings with status "pending" or "confirmed"
    // (cancelled, completed, and no_show bookings should not block available slots)
    const bookingRanges = [];
    bookings
      .filter((b) => {
        const status = (b?.status || "").toLowerCase();
        // Only include bookings that are pending or confirmed
        if (!["confirmed", "pending"].includes(status)) return false;

        // Convert UTC booking times to provider's timezone
        if (b?.startAt) {
          // Parse UTC time and convert to provider timezone
          const utcMoment = moment.utc(b.startAt);
          const localMoment = utcMoment.tz(timezone);
          const bookingDateStr = localMoment.format("YYYY-MM-DD");

          // Only process bookings on the selected date (in provider's timezone)
          if (bookingDateStr === selectedDateStr) {
            return true;
          }
        }
        return false;
      })
      .forEach((b) => {
        // Convert UTC booking times to provider's timezone
        const utcStartMoment = moment.utc(b.startAt);
        const localStartMoment = utcStartMoment.tz(timezone);
        const localStartTime = localStartMoment.format("HH:mm");
        const [startH, startM] = localStartTime.split(":").map(Number);
        const startMinutes = startH * 60 + startM;

        // Calculate end time in provider's timezone
        let endMinutes;
        if (b?.endAt) {
          const utcEndMoment = moment.utc(b.endAt);
          const localEndMoment = utcEndMoment.tz(timezone);
          const localEndTime = localEndMoment.format("HH:mm");
          const [endH, endM] = localEndTime.split(":").map(Number);
          endMinutes = endH * 60 + endM;
        } else {
          // Fallback: calculate from duration
          let bookingDuration = duration;
          if (b?.ServiceDetail?.duration || b?.Service?.duration) {
            bookingDuration = b?.ServiceDetail?.duration || b?.Service?.duration;
          }
          endMinutes = startMinutes + bookingDuration;
        }

        // Store the booking range [startMinutes, endMinutes)
        bookingRanges.push({ start: startMinutes, end: endMinutes });
      });

    // Filter out time slots that would overlap with any existing booking
    // A slot overlaps if: slotStart < bookingEnd AND slotEnd > bookingStart
    times = times.filter((timeStr) => {
      const [h, m] = timeStr.split(":").map(Number);
      const slotStartMinutes = h * 60 + m;
      const slotEndMinutes = slotStartMinutes + duration;

      // Check if this slot overlaps with any existing booking
      for (const booking of bookingRanges) {
        // Overlap occurs if: slotStart < bookingEnd AND slotEnd > bookingStart
        if (slotStartMinutes < booking.end && slotEndMinutes > booking.start) {
          return false; // This slot overlaps, filter it out
        }
      }
      return true; // No overlap, keep this slot
    });

    // Calculate all blocked time ranges based on provider's blocked times
    // Blocked times are stored in UTC and need to be converted to provider's timezone
    const blockedRanges = [];
    blockedTimes
      .filter((b) => {
        // Convert UTC blocked time to provider's timezone
        if (b?.startAt) {
          const utcMoment = moment.utc(b.startAt);
          const localMoment = utcMoment.tz(timezone);
          const blockedDateStr = localMoment.format("YYYY-MM-DD");

          // Also check if blocked time ends on the selected date (for multi-day blocks)
          let blockedEndDateStr = "";
          if (b?.endAt) {
            const utcEndMoment = moment.utc(b.endAt);
            const localEndMoment = utcEndMoment.tz(timezone);
            blockedEndDateStr = localEndMoment.format("YYYY-MM-DD");
          }

          // Include if blocked time starts on selected date OR ends on/after selected date
          // This handles multi-day blocked periods
          if (blockedDateStr === selectedDateStr ||
            (blockedDateStr <= selectedDateStr && blockedEndDateStr >= selectedDateStr)) {
            return true;
          }
        }
        return false;
      })
      .forEach((b) => {
        // Convert UTC blocked times to provider's timezone
        const utcStartMoment = moment.utc(b.startAt);
        const localStartMoment = utcStartMoment.tz(timezone);
        const blockedDateStr = localStartMoment.format("YYYY-MM-DD");

        let startMinutes;
        // If blocked time started before selected date, treat start as midnight
        if (blockedDateStr < selectedDateStr) {
          startMinutes = 0; // Start of day
        } else {
          const localStartTime = localStartMoment.format("HH:mm");
          const [startH, startM] = localStartTime.split(":").map(Number);
          startMinutes = startH * 60 + startM;
        }

        // Calculate end time in provider's timezone
        let endMinutes;
        if (b?.endAt) {
          const utcEndMoment = moment.utc(b.endAt);
          const localEndMoment = utcEndMoment.tz(timezone);
          const blockedEndDateStr = localEndMoment.format("YYYY-MM-DD");

          // If blocked time ends after selected date, treat end as end of day
          if (blockedEndDateStr > selectedDateStr) {
            endMinutes = 24 * 60; // End of day
          } else {
            const localEndTime = localEndMoment.format("HH:mm");
            const [endH, endM] = localEndTime.split(":").map(Number);
            endMinutes = endH * 60 + endM;
          }
        } else {
          // Fallback: assume 1 hour blocked if no end time
          endMinutes = startMinutes + 60;
        }

        // Store the blocked range [startMinutes, endMinutes)
        blockedRanges.push({ start: startMinutes, end: endMinutes });
      });

    // Filter out time slots that would overlap with any blocked time
    times = times.filter((timeStr) => {
      const [h, m] = timeStr.split(":").map(Number);
      const slotStartMinutes = h * 60 + m;
      const slotEndMinutes = slotStartMinutes + duration;

      // Check if this slot overlaps with any blocked time
      for (const blocked of blockedRanges) {
        // Overlap occurs if: slotStart < blockedEnd AND slotEnd > blockedStart
        if (slotStartMinutes < blocked.end && slotEndMinutes > blocked.start) {
          return false; // This slot overlaps with blocked time, filter it out
        }
      }
      return true; // No overlap with blocked times, keep this slot
    });

    // Debug: Log blocked time filtering
    if (blockedTimes.length > 0) {
      console.log(`[DateSelection] Raw blocked times received:`, blockedTimes);
      console.log(`[DateSelection] Blocked time ranges for ${selectedDateStr}:`, blockedRanges);
      console.log(`[DateSelection] Provider timezone:`, timezone);
    }

    // Remove past if today
    const now = new Date();
    if (isSameDay(now, day)) {
      const current = now.getHours() * 60 + now.getMinutes();
      const beforeFilter = times.length;
      times = times.filter((t) => {
        const [h, m] = t.split(":").map(Number);
        const timeInMinutes = h * 60 + m;
        return timeInMinutes >= current;
      });
      // Debug: Log if times were filtered out
      if (process.env.NODE_ENV === 'development' && beforeFilter > times.length) {
        console.log(`[DateSelection] Filtered out ${beforeFilter - times.length} past time slots (current time: ${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')})`);
      }
    }

    // Filter slots within minimum advance notice window
    if (minimumAdvanceNoticeHours > 0) {
      times = times.filter((t) => {
        const [h, m] = t.split(":").map(Number);
        const slotDateTime = new Date(day);
        slotDateTime.setHours(h, m, 0, 0);
        const hoursUntilSlot = (slotDateTime - now) / (1000 * 60 * 60);
        return hoursUntilSlot >= minimumAdvanceNoticeHours;
      });
    }

    // Sort times numerically (by converting to minutes for comparison)
    times.sort((a, b) => {
      const [ah, am] = a.split(":").map(Number);
      const [bh, bm] = b.split(":").map(Number);
      return (ah * 60 + am) - (bh * 60 + bm);
    });

    // Debug: Log final times
    if (process.env.NODE_ENV === 'development' && dayAvail.length > 0) {
      console.log(`[DateSelection] Final available times:`, times);
    }

    return times;
  }, [availability, bookings, blockedTimes, serviceDuration, extractDateTimeParts, timezone, minimumAdvanceNoticeHours]);

  const availableTimes = useMemo(() => {
    if (!selectedDate) return [];
    return getTimesForDay(selectedDate);
  }, [selectedDate, availability, bookings, blockedTimes, serviceDuration, getTimesForDay]);

  // Jump to next available date
  const handleJumpToNextAvailable = () => {
    if (!selectedDate) return;
    const idx = allDays.findIndex((d) => isSameDay(d, selectedDate));
    for (let i = idx + 1; i < allDays.length; i++) {
      if (getTimesForDay(allDays[i]).length > 0) {
        setSelectedDate(allDays[i]);
        return;
      }
    }
    setShowNoDatesPopup(true);
  };

  // Scroll selected into view (horizontal only)
  useEffect(() => {
    if (!selectedDate || !sliderRef.current) return;
    const idx = allDays.findIndex((d) => isSameDay(d, selectedDate));
    if (idx >= 0) {
      const child = sliderRef.current.children[idx];
      if (child)
        child.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
    }
    setSelectedTime("");
  }, [selectedDate, allDays, setSelectedTime]);

  // Month label
  const monthYearText = selectedDate
    ? format(selectedDate, "MMMM yyyy")
    : format(new Date(), "MMMM yyyy");

  // Calendar Modal state
  const [showCalendar, setShowCalendar] = useState(false);
  const [month, setMonth] = useState(new Date());

  // Custom Popup state
  const [showNoDatesPopup, setShowNoDatesPopup] = useState(false);

  return (
    <div>
      {/* No Dates Popup */}
      {showNoDatesPopup && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center transform transition-all scale-100 animate-in zoom-in-95 duration-200 border border-purple-100">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faCalendarXmark} className="text-3xl text-gray-900" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Availability Found</h3>
            <p className="text-gray-600 mb-6">
              There are no available slots for this service in the next 12 months. Please try selecting a different service or contact the provider directly.
            </p>
            <button
              onClick={() => setShowNoDatesPopup(false)}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors shadow-lg shadow-purple-200"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Tip */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end pb-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-gray-800 text-sm">
            📝 Tip: Click on any day and you'll jump to the next available slot.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4 justify-center sm:justify-start">
        <h2 className="text-xl font-bold text-gray-900">{monthYearText}</h2>
        <button
          onClick={() => setShowCalendar(true)}
          className="text-gray-700 hover:text-gray-900"
        >
          <FontAwesomeIcon icon={faCalendarDays} />
        </button>
      </div>

      {showCalendar && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-2">
          <div className="bg-white p-4 rounded-lg relative w-full max-w-md mx-auto shadow-xl">
            <button
              onClick={() => setShowCalendar(false)}
              className="absolute top-2 right-2 text-gray-700 hover:text-gray-900 font-bold"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <DayPicker
              mode="single"
              selected={selectedDate}
              month={month}
              onMonthChange={setMonth}
              onSelect={(day) => {
                if (day) {
                  setSelectedDate(day);
                  setShowCalendar(false);
                }
              }}
              disabled={{ before: new Date() }}
              styles={{ day: { color: "#111827" }, weekday: { color: "#111827" } }}
              components={{
                Nav: (navProps) => (
                  <CustomCalendarNav {...navProps} month={month} />
                ),
                CaptionLabel: () => null,
              }}
              modifiersClassNames={{ selected: "selected-day" }}
            />
          </div>
        </div>
      )}

      {/* Infinite scroll slider */}
      <div
        ref={sliderRef}
        className="flex overflow-x-auto no-scrollbar space-x-4 px-2 py-2 mb-4"
      >
        {allDays.map((day) => {
          const selected = selectedDate && isSameDay(day, selectedDate);
          const avail = getTimesForDay(day).length > 0;
          return (
            <div
              key={day.toString()}
              className="inline-block min-w-[60px] text-center cursor-pointer"
              onClick={() => setSelectedDate(day)}
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto 
                  ${selected ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-900"} 
                  ${!avail ? "opacity-50" : ""}
                  ${!selected && avail ? "hover:bg-gray-200 cursor-pointer" : ""}`}
              >
                {format(day, "d")}
              </div>
              <div className={`text-sm mt-1 ${selected ? "text-purple-600 font-semibold" : "text-gray-600"}`}>
                {format(day, "EEE")}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time slots */}
      <div className="mb-4">
        {selectedDate ? (
          availableTimes.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {availableTimes.map((time) => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={`py-2 rounded-md border transition-colors
                    ${selectedTime === time
                      ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold"
                      : "border-gray-300 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50"
                    }`}
                >
                  {formatTime12Hour(time)}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-gray-600 text-center">
              <p>No times available for {format(selectedDate, "PP")}.</p>
              <button
                onClick={handleJumpToNextAvailable}
                className="mt-2 px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors"
              >
                Jump to Next Available Date
              </button>
            </div>
          )
        ) : (
          <p className="text-gray-600">Please select a date.</p>
        )}
      </div>
    </div>
  );
}
